import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { AVMark, AVWordmark } from "./Logo";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  LayoutGrid,
  Image,
  Wand2,
  Clapperboard,
  Film,
  Sparkles,
  ListVideo,
  Trophy,
  PartyPopper,
  Megaphone,
  ScrollText,
  BookOpen,
  Newspaper,
  LogOut,
  Building2,
  Settings2,
} from "lucide-react";

const nav = [
  { to: "/", label: "TV Wall", icon: LayoutGrid, end: true },
  { to: "/media", label: "Media Library", icon: Image },
  { to: "/ai-slides", label: "AI Slides", icon: Wand2 },
  { to: "/ai-clips", label: "AI Clips", icon: Clapperboard },
  { to: "/ai-director", label: "AI Video Director", icon: Film },
  { to: "/weekly-ads", label: "Weekly Ads", icon: Newspaper },
  { to: "/samples", label: "Sample Ads", icon: Sparkles },
  { to: "/playlists", label: "Playlists", icon: ListVideo },
  { to: "/sports", label: "Live Sports", icon: Trophy },
  { to: "/themes", label: "Seasonal Themes", icon: PartyPopper },
  { to: "/ads", label: "Timed Ads", icon: Megaphone },
  { to: "/logs", label: "Activity Logs", icon: ScrollText },
  { to: "/guide", label: "Install Guide", icon: BookOpen },
];

export default function Layout() {
  const { user, logout, businesses, businessId, switchBusiness } = useAuth();
  const location = useLocation();

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-card sticky top-0 h-screen">
        <div className="flex items-center gap-3 px-5 py-5 border-b border-border">
          <AVMark size={46} />
          <AVWordmark size="lg" />
        </div>
        {/* Business switcher */}
        {businesses.length > 0 && (
          <div className="px-3 pt-3" data-testid="business-switcher">
            {businesses.length > 1 ? (
              <Select value={businessId} onValueChange={switchBusiness}>
                <SelectTrigger className="w-full h-11 bg-secondary/40" data-testid="business-select-trigger">
                  <div className="flex items-center gap-2 min-w-0">
                    <Building2 className="w-4 h-4 text-primary flex-shrink-0" />
                    <SelectValue placeholder="Select business" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {businesses.map((b) => (
                    <SelectItem key={b.id} value={b.id} data-testid={`business-option-${b.id}`}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="flex items-center gap-2 px-3 h-11 rounded-md bg-secondary/40 border border-border" data-testid="business-single">
                <Building2 className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-sm font-medium truncate">{businesses[0].name}</span>
              </div>
            )}
          </div>
        )}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-secondary hover:text-white"
                }`
              }
            >
              <item.icon className="w-[18px] h-[18px]" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <NavLink to="/brand" data-testid="nav-brand"
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 mb-1 rounded-md text-sm font-medium transition-colors ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary hover:text-white"}`}>
            <Settings2 className="w-[18px] h-[18px]" /> Brand Settings
          </NavLink>
          <div className="flex items-center justify-between px-3 py-2">
            <div>
              <p className="text-sm font-medium">{user?.name}</p>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{user?.role}</p>
            </div>
            <button
              data-testid="logout-button"
              onClick={logout}
              className="p-2 rounded-md text-muted-foreground hover:text-destructive hover:bg-secondary transition-colors duration-200"
            >
              <LogOut className="w-[18px] h-[18px]" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden flex items-center justify-between px-4 py-2.5 border-b border-border bg-card sticky top-0 z-30">
          <div className="flex items-center gap-2.5 min-w-0">
            <AVMark size={38} />
            {businesses.length > 1 ? (
              <Select value={businessId} onValueChange={switchBusiness}>
                <SelectTrigger className="h-9 border-0 bg-secondary/40 px-2 text-xs" data-testid="business-select-mobile">
                  <Building2 className="w-3.5 h-3.5 text-primary mr-1" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {businesses.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <AVWordmark size="md" />
            )}
          </div>
          <button onClick={logout} data-testid="logout-button-mobile" className="text-muted-foreground flex-shrink-0">
            <LogOut className="w-5 h-5" />
          </button>
        </header>
        <main className="flex-1 min-w-0 pb-16 md:pb-0">
          <Outlet />
        </main>
        {/* Mobile bottom nav */}
        <nav className="md:hidden sticky bottom-0 z-30 grid grid-cols-11 border-t border-border bg-card">
          {nav.map((item) => {
            const active =
              item.end ? location.pathname === "/" : location.pathname.startsWith(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-mobile-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`flex flex-col items-center gap-1 py-2 text-[10px] ${
                  active ? "text-primary" : "text-muted-foreground"
                }`}
              >
                <item.icon className="w-5 h-5" />
              </NavLink>
            );
          })}
        </nav>
      </div>

      {/* Persistent brand mark — shown in the corner on every page */}
      <div className="hidden md:flex fixed bottom-3 right-5 z-20 flex-col items-end pointer-events-none select-none" data-testid="powered-by-badge">
        <span className="text-2xl font-display font-extrabold tracking-tight text-foreground/70 leading-none">SignHub</span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-0.5">powered by A V Tech</span>
      </div>
    </div>
  );
}
